'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function GuzellikMerkezlerininBotoksYapmaYetkisi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/guzellik-merkezlerinin-botoks-yapma-yetkisi-yasal-durum-ve-klinik-gercekler';
  const pageTitle = 'Güzellik Merkezlerinin Botoks Yapma Yetkisi: Yasal Durum ve Klinik Gerçekler';
  const pageDescription = 'Güzellik merkezleri botoks yapabilir mi? Sağlık Bakanlığı yönetmelikleri, yasal yetki sınırları ve hastaların bilinmesi gerekenler.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Güzellik Merkezlerinin Botoks Yapma Yetkisi', href: '/tr/blog/guzellik-merkezlerinin-botoks-yapma-yetkisi-yasal-durum-ve-klinik-gercekler' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Güzellik merkezlerinin botoks yapma yetkisi ve yasal durum"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Yasal Durum
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Güzellik Merkezlerinin Botoks Yapma Yetkisi: Yasal Durum ve Klinik Gerçekler
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>11 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>13 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Son yıllarda botoks uygulamaları tıp muayenehanelerinden çıkıp kuaför ve güzellik merkezlerine kadar genişlemiş durumda.
                Ancak bu yaygınlaşma beraberinde önemli bir soru getiriyor:
              </p>

              <div className="bg-amber-50 border-l-4 border-amber-400 p-6 mb-6">
                <p className="text-amber-800 font-medium text-lg">
                  "Güzellik merkezleri botoks yapabilir mi?"
                </p>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Cevap basit görünse de, hem hukuki hem tıbbi açıdan son derece karmaşık bir konudur.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Yasal Tanım: Botoks Bir Tıbbi Uygulamadır
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botulinum toksini enjekte etmek, yani kas içine tıbbi bir madde uygulamak, Türkiye Cumhuriyeti yasalarına göre tıbbi bir işlemdir.
                Bu nedenle yalnızca hekimler tarafından yapılabilir.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h4 className="font-semibold text-blue-900 mb-4">📜 Dayanak:</h4>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Sağlık Bakanlığı "Ayakta Teşhis ve Tedavi Yapılan Özel Sağlık Kurumları Hakkında Yönetmelik" (Madde 9)</li>
                  <li>1219 sayılı Tababet ve Şuabatı San'atlarının Tarzı İcrasına Dair Kanun (Madde 1 ve 2)</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Bu kanunlara göre, her türlü enjeksiyon, ilaç uygulaması veya dokusal müdahale yalnızca hekim tarafından veya hekim gözetiminde yapılabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Güzellik Merkezlerinin Yetki Kapsamı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Güzellik merkezleri, Sağlık Bakanlığı'na değil belediyelere bağlıdır ve yetki alanları kozmetik ve yüzeysel işlemlerle sınırlıdır.
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Merkez Tipi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yasal Yetki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Örnek İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Güzellik salonu</td>
                      <td className="border border-gray-300 px-4 py-3">Kozmetik ve bakım</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt temizliği, maskeler, epilasyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Estetik güzellik merkezi</td>
                      <td className="border border-gray-300 px-4 py-3">Yüzeysel cilt bakımı</td>
                      <td className="border border-gray-300 px-4 py-3">Leke bakımı, kimyasal peeling</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tıp merkezi / muayenehane</td>
                      <td className="border border-gray-300 px-4 py-3">Tıbbi uygulama yetkili</td>
                      <td className="border border-gray-300 px-4 py-3">Botoks, dolgu, mezoterapi</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  📍 Sonuç: Güzellik merkezlerinde botoks yapılması yasal değildir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Yasa İhlalinin Sonuçları
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yetkisiz botoks uygulaması "tıbbi müdahale suçu" olarak değerlendirilir.
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Türk Ceza Kanunu (TCK Madde 187) kapsamında "izinsiz ilaç uygulamak" suçtur.</li>
                  <li>Hem işlemi yapan hem işlemi yaptıran idari ve hukuki sorumluluk altındadır.</li>
                  <li>Yani hasta zarar görmese bile, "yasal olmayan uygulamaya katılmak" da sorumluluk doğurur.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Sağlık Bakanlığı Uyarısı
              </h2>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <p className="text-green-800 font-medium">
                  Sağlık Bakanlığı (2024) resmî açıklamasında:
                </p>
                <p className="text-green-800 italic mt-2">
                  "Botulinum toksin uygulamaları yalnızca hekimler tarafından, sağlık kuruluşu bünyesinde ve onaylı ürünlerle yapılabilir."
                </p>
                <p className="text-green-800 mt-4">
                  Ayrıca ürün ambalajlarının izinsiz kullanımı, sahte ve kaçak ürün riskini doğurur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Tıbbi Riskler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas asimetrisi</td>
                      <td className="border border-gray-300 px-4 py-3">Yanlış enjeksiyon noktası</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz kapağı düşüklüğü</td>
                      <td className="border border-gray-300 px-4 py-3">Kas planı bilgisi olmayan uygulama</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Alerjik reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Ürün içeriğinin bilinmemesi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Steril olmayan ortamda uygulama</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sinir hasarı</td>
                      <td className="border border-gray-300 px-4 py-3">Anatomik bilgisizlik</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Botoks bir "güzellik enjeksiyonu" değil, nörotoksin temelli bir tıbbi ilaçtır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Muayenehane ve Güzellik Merkezi Farkı
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-blue-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Kriter</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Tıp Muayenehanesi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Güzellik Merkezi</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Uygulayıcı</td>
                      <td className="border border-gray-300 px-4 py-3">Uzman hekim</td>
                      <td className="border border-gray-300 px-4 py-3">Kozmetolog / teknisyen</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Steril koşul</td>
                      <td className="border border-gray-300 px-4 py-3">Cerrahi standardında</td>
                      <td className="border border-gray-300 px-4 py-3">Yüzeysel hijyen</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Acil ekipman</td>
                      <td className="border border-gray-300 px-4 py-3">Mevcut</td>
                      <td className="border border-gray-300 px-4 py-3">Bulunmaz</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Ürün kaynağı</td>
                      <td className="border border-gray-300 px-4 py-3">Sağlık Bakanlığı onaylı</td>
                      <td className="border border-gray-300 px-4 py-3">Serbest piyasadan</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Yani "aynı işlem gibi görünse de," altyapı tamamen farklıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Hasta Güvenliği Açısından
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, sinir-kas iletişimini etkileyen bir ilaçtır.
                Uygulamada milimetrik hata bile kalıcı komplikasyonlara neden olabilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  Dr. Barış Çin uyarıyor:
                </p>
                <p className="text-red-800 italic mt-2">
                  "Bir şişe botoksun değeri yüzünden sağlığınızı riske atmayın.
                  Uygulayıcı hekim değilse, işlem botoks değil, risk enjeksiyonudur."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Etik Boyut
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Estetik tıpta temel kural şudur: Zarar vermeme ilkesi (non maleficence).
                Hekim olmayan birinin enjeksiyon yapması, hem bu ilkeye hem de mesleki etik kurallara aykırıdır.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  "Güzellik bir hak, ama bilinç bir sorumluluktur."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Hastalar İçin Kontrol Listesi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks yaptırmadan önce şunları sorgula:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>🏥 Uygulama yeri Sağlık Bakanlığı onaylı mı?</li>
                  <li>👨‍⚕️ İşlemi yapan hekim mi, uzman branşı nedir?</li>
                  <li>💉 Ürün kutusu ve seri numarası gösteriliyor mu?</li>
                  <li>📋 Bilgilendirme formu ve onam imzalatılıyor mu?</li>
                </ul>
                <p className="text-green-800 mt-4 font-medium">
                  Eğer bu soruların cevabı "hayır" ise, işlem yasal ve tıbbi açıdan geçersizdir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Güzellik merkezinde botoks yaptırabilir miyim?</h3>
                  <p className="text-gray-600">Hayır. Yalnızca doktor yetkilidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Doktor gözetiminde yapılsa olur mu?</h3>
                  <p className="text-gray-600">Hekim doğrudan uygulamıyorsa, yasal değildir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Merkez yetkili olduğunu söylüyor, nasıl anlarım?</h3>
                  <p className="text-gray-600">Sağlık Bakanlığı web sitesinden kurum sorgulaması yapılabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Sahte botoks nasıl anlaşılır?</h3>
                  <p className="text-gray-600">Ambalajda lot numarası ve orijinal etiket bulunmalıdır.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hangi hekimler botoks yapabilir?</h3>
                  <p className="text-gray-600">Plastik cerrahi, dermatoloji, estetik cerrahi uzmanları ve eğitim almış diğer hekimler.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Yasal olmayan uygulamada ne olur?</h3>
                  <p className="text-gray-600">Hem uygulayıcı hem hasta hukuki sorumluluk altındadır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Yasal Kaynaklar
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>1219 Sayılı Tababet Kanunu (Madde 1–2)</li>
                  <li>Ayakta Teşhis ve Tedavi Yönetmeliği (Madde 9–12)</li>
                  <li>Sağlık Bakanlığı Resmî Açıklaması, 2024</li>
                  <li>TETD Etik Kılavuzu, 2024</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks görünüşte basit, ama biyolojik olarak karmaşık bir ilaçtır.
                Uygulayıcının hekim olmaması, tüm süreci riskli hale getirir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Botoksu değil, botoksu yapan kişiyi seçin.
                  Sağlığınız güzelliğinizden daha değerlidir."
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#güzellikmerkezi</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#sağlıkhukuku</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetikgüvenlik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Güvenli Botoks Uygulaması İçin Randevu Alın</h3>
          <p className="mb-6">Uzman doktorumuzdan güvenli ve yasal botoks uygulaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli botoks uygulaması hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Botoksu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Güvenli ürün nasıl tespit edilir? Orijinal botoks ürünlerini tanıma rehberi.</p>
            </a>
            <a
              href="/tr/blog/botoksun-alerji-riski-gercek-tehlike-mi-yanlis-algi-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoksun Alerji Riski</h4>
              <p className="text-gray-600 text-sm">Botoks alerji yapar mı? Gerçek tehlike mi, yanlış algı mı?</p>
            </a>
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
